// Spanish quiz (web47): display text for the pricing cards.
//
// Plan data (getPlans & co. in LandingPage.tsx) carries English display
// strings next to the ids, prices and checkout links. The cards translate them
// here at render time, so the plan objects (and everything that reads them for
// checkout or analytics) stay unchanged. Each known English string maps to a
// key in locales/<lng>/pricing.json whose English value is the same string.
import type { TFunction } from "i18next";
import i18n, { reportMissingKey } from "../i18n";

const PLAN_TEXT_KEYS: Record<string, string> = {
  "Getting Serious": "pricing.plan.serious.title",
  "Fully Committed": "pricing.plan.committed.title",
  "You're still feeling things out, but you're ready to show up consistently and start putting in some real effort.":
    "pricing.plan.serious.description",
  "You’re both still feeling things out, but you’re ready to show up for consistency.":
    "pricing.plan.serious.descriptionTwo",
  "This is your lifestyle now. You're looking for structure and long-term support to help you stay on track.":
    "pricing.plan.committed.description",
  "BUILT FOR LONG-TERM RESULTS": "pricing.plan.committed.badge",
  "FOR LONG-TERM RESULTS": "pricing.plan.committed.badgeTwo",
  "/ month": "pricing.plan.perMonth",
  "/ mo": "pricing.plan.perMo",
  "(billed annually)": "pricing.trial.billedAnnually",
  "per person": "pricing.gymBuddy.perPerson",
};

// Strings built around a price or percentage: [pattern, key, variable name].
const PLAN_TEXT_PATTERNS: ReadonlyArray<[RegExp, string, string]> = [
  [/^per person \(billed annually at \$([\d.,]+)\)$/, "pricing.plan.perPersonBilledAnnually", "total"],
  [/^vs\. (\$[\d.,]+)\/mo each on separate plans$/, "pricing.gymBuddy.vsSeparatePlans", "price"],
  [/^vs\. (\$[\d.,]+)\/mo for a 1-person plan$/, "pricing.gymBuddy.vsOnePersonPlan", "price"],
  [/^(\d+(?:\.\d+)?%) OFF$/, "pricing.trial.percentOff", "percent"],
];

/** Translate one English display string from a CheckoutPlan. */
export function planText(t: TFunction, text: string): string;
export function planText(t: TFunction, text: string | undefined): string | undefined;
export function planText(t: TFunction, text: string | undefined): string | undefined {
  if (!text) return text;
  const key = PLAN_TEXT_KEYS[text];
  if (key) return t(key);
  for (const [pattern, patternKey, name] of PLAN_TEXT_PATTERNS) {
    const match = text.match(pattern);
    if (match) return t(patternKey, { [name]: match[1] });
  }
  // A plan string this table does not know: show it as is, and flag it like
  // any missing key (the strict E2E build throws).
  if (i18n.language !== "en") reportMissingKey(i18n.language, `pricing plan text "${text}"`);
  return text;
}

/** Locale for dates and numbers on the pricing cards ("en-US" in English, as before). */
export function displayLocale(language: string): string {
  return language === "es" ? "es-MX" : "en-US";
}

/** Short timeline date ("Oct 28" / "28 oct"), `daysFromToday` days from now. */
export function shortDate(language: string, daysFromToday = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromToday);
  return d.toLocaleDateString(displayLocale(language), { month: "short", day: "numeric" });
}
